import { useRef, useState } from "react";
import { Paperclip, X, Loader2, FileText, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * MessageAttachmentPicker — the paperclip button + chip preview that
 * sits inside a message composer. Handles the full upload lifecycle
 * (pick → POST /uploads/attachment → hold the returned URL until the
 * parent form is submitted).
 *
 * Props:
 *   attachments — controlled array of {url, filename, content_type, size}
 *   onChange(next) — parent commits the new list on add/remove
 *   disabled — while the composer itself is sending
 *
 * Accepts up to 5 attachments per message and enforces the same 10 MB
 * ceiling the backend does; failures surface as toasts so the user
 * doesn't wonder why the file "disappeared".
 */
const MAX_ATTACHMENTS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = new Set([
  "application/pdf",
  "image/jpeg", "image/png", "image/webp", "image/gif",
]);

export default function MessageAttachmentPicker({
  attachments = [], onChange, disabled, testIdPrefix = "msg-attach",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file after remove
    if (!files.length) return;

    // Front-guard: type + size + count. Backend also enforces but a
    // toast is nicer than a silent 400 after the upload completes.
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_ATTACHMENTS} attachments per message`);
      return;
    }
    const eligible = files.slice(0, remaining).filter((f) => {
      if (!ALLOWED.has(f.type)) {
        toast.error(`${f.name}: only PDF, JPG, PNG, WEBP, or GIF are supported`);
        return false;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over 10 MB`);
        return false;
      }
      return true;
    });
    if (!eligible.length) return;

    setBusy(true);
    try {
      // Upload sequentially so 3 x 8MB files don't all hit the wire at once.
      const next = [...attachments];
      for (const f of eligible) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/attachment", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        next.push({
          url: data.url,
          filename: data.filename || f.name,
          content_type: data.content_type || f.type,
          size: data.size || f.size,
        });
      }
      onChange(next);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Upload failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = (idx) => {
    const next = attachments.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy || attachments.length >= MAX_ATTACHMENTS}
        title="Attach a PDF or image (max 10 MB)"
        data-testid={`${testIdPrefix}-button`}
        className="p-2 rounded-full transition-colors disabled:opacity-40"
        style={{ color: "var(--avt-purple)" }}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Paperclip className="h-4 w-4" />}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="application/pdf,image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handlePick}
        data-testid={`${testIdPrefix}-input`}
      />
      {attachments.length > 0 && (
        <div className="w-full flex flex-wrap gap-1.5 mt-2"
             data-testid={`${testIdPrefix}-chips`}>
          {attachments.map((a, i) => (
            <AttachmentChip key={a.url + i} attachment={a}
                            onRemove={() => remove(i)}
                            testId={`${testIdPrefix}-chip-${i}`} />
          ))}
        </div>
      )}
    </>
  );
}

function AttachmentChip({ attachment, onRemove, testId }) {
  const isImage = (attachment.content_type || "").startsWith("image/");
  const Icon = isImage ? ImageIcon : FileText;
  return (
    <span
      className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-xs border"
      style={{
        backgroundColor: "var(--sand)",
        borderColor: "var(--border-warm)",
        color: "var(--ink)",
      }}
      data-testid={testId}
    >
      <Icon className="h-3 w-3" style={{ color: "var(--avt-purple)" }} />
      <span className="max-w-[160px] truncate">{attachment.filename}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${attachment.filename}`}
        className="p-0.5 rounded-full hover:bg-white/60"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
